import { useEffect, useState } from 'react';
import ListView from 'src/layouts/ListView';
import { Helmet } from 'react-helmet';
import DraftSubmissions from './DraftSubmissions';
import SubmittedSubmissions from './SubmittedSubmissions';
import RevisionSubmissions from './RevisionSubmissions';
import UnsubmittedSubmissions from './UnsubmittedSubmissions';
import CompletedSubmissions from './CompletedSubmissions';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { BadgeVariants, Button } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRotateLeft,
  faFileCheck,
  faListCheck,
  faPenToSquare,
  faPlus,
  faUserCircle,
  IconDefinition
} from '@fortawesome/pro-regular-svg-icons';
import { useKeycloak } from 'src/components/AuthHOC';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IWorkPhase,
  MY_SUBMISSIONS_TABS,
  MySubmissionsTab
} from '@publishing-engine/domain/models';
import {
  SUBMISSION_STATUS_MAP,
  MY_SUBMISSIONS_TOTALS_QUERY_KEY
} from 'src/constants';
import ScrollToTopButton from '../Organization/public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import { fetchMySubmissionsTotals } from 'src/api/queries';
import Footer from '../../components/Footer';

export const STATUS_MAP: Record<
  IWorkPhase | (string & {}),
  { icon: IconDefinition | null; variant: BadgeVariants; label: string }
> = {
  ...SUBMISSION_STATUS_MAP,
  draft: {
    icon: null,
    variant: 'attention',
    label: 'Draft'
  },
  revision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Revision'
  },
  assignment: {
    icon: faUserCircle,
    variant: 'neutral',
    label: 'Awaiting Assignment'
  },
  initialAssessment: {
    icon: faListCheck,
    variant: 'neutral',
    label: 'Initial Assessment'
  },
  unsubmitted: {
    icon: faArrowRotateLeft,
    variant: 'attention',
    label: 'Unsubmitted'
  },
  preProduction: {
    icon: faFileCheck,
    variant: 'success',
    label: 'Pre Production'
  }
} as const;

const TABS = MY_SUBMISSIONS_TABS;
type Tab = MySubmissionsTab;

const TAB_LABEL_MAP: Record<Tab, string> = {
  [TABS.DRAFTS]: 'Drafts',
  [TABS.SUBMITTED]: 'Active',
  [TABS.REVISIONS]: 'Revisions',
  [TABS.UNSUBMITTED]: 'Unsubmitted',
  [TABS.COMPLETED]: 'Completed'
};

const renderTab = (tab: Tab) => {
  switch (tab) {
    case TABS.DRAFTS:
      return <DraftSubmissions />;
    case TABS.SUBMITTED:
      return <SubmittedSubmissions />;
    case TABS.REVISIONS:
      return <RevisionSubmissions />;
    case TABS.UNSUBMITTED:
      return <UnsubmittedSubmissions />;
    case TABS.COMPLETED:
      return <CompletedSubmissions />;
    default:
      return null;
  }
};

const MySubmissionsList = () => {
  const { authRealm } = useKeycloak();

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const submissionStatus = searchParams.get('submissionType') || '';
  const selectedTab = submissionStatus
    ? submissionStatus === 'my-submissions-draft'
      ? TABS.DRAFTS
      : submissionStatus === 'my-submissions-revision'
      ? TABS.REVISIONS
      : submissionStatus === 'my-submissions-unsubmitted'
      ? TABS.UNSUBMITTED
      : submissionStatus === 'my-submissions-completed'
      ? TABS.COMPLETED
      : TABS.SUBMITTED
    : TABS.DRAFTS;

  const [activeTab, setActiveTab] = useState<Tab>(selectedTab);

  const { data: totals } = useQuery({
    queryKey: [MY_SUBMISSIONS_TOTALS_QUERY_KEY],
    queryFn: fetchMySubmissionsTotals
  });

  const TAB_COUNT_MAP: Record<Tab, number> = {
    [TABS.DRAFTS]: totals?.drafts ?? 0,
    [TABS.SUBMITTED]: totals?.submitted ?? 0,
    [TABS.REVISIONS]: totals?.revisions ?? 0,
    [TABS.UNSUBMITTED]: totals?.unsubmitted ?? 0,
    [TABS.COMPLETED]: totals?.completed ?? 0
  };

  const getTabLabel = (tab: Tab) => {
    const count = TAB_COUNT_MAP[tab];
    return count !== undefined ? `${TAB_LABEL_MAP[tab]} (${count})` : TAB_LABEL_MAP[tab];
  };

  useEffect(() => {
    submissionStatus &&
      navigate(`/app/my-submissions?${searchParams.toString()}`);
    setActiveTab(selectedTab);
  }, [submissionStatus]);

  return (
    <Box
      id="scrollable-wrapper"
      height="100%"
      overflowY="auto"
      overflowX="hidden"
    >
      <Box padding={{ base: 3, md: 5 }} minHeight="100%">
        <Flex
          flexDirection="column"
          width="100%"
          alignItems="flex-start"
          gap={3}
        >
          <Helmet>
            <title>My Submissions</title>
          </Helmet>
          <Box textStyle="headingSection" width="100%">
            <Flex
              justifyContent="space-between"
              alignItems={{ base: 'flex-start', md: 'center' }}
              flexDirection={{ base: 'column', md: 'row' }}
              gap={{ base: 3, md: 0 }}
              width="100%"
              mb={2}
            >
              <Text>My Submissions</Text>
              {authRealm && (
                <Button
                  variant="primary"
                  width={{ base: '100%', md: 'auto' }}
                  onClick={() => navigate('/app/journal-index')}
                >
                  <Flex gap={2} alignItems="center">
                    <FontAwesomeIcon icon={faPlus} />
                    <Text>New Submission</Text>
                  </Flex>
                </Button>
              )}
            </Flex>
          </Box>
          <Flex
            gap={2}
            alignItems="center"
            flexWrap={{ base: 'nowrap', md: 'wrap' }}
            overflowX={{ base: 'auto', md: 'visible' }}
            borderBottom="1px solid"
            borderColor="neutralPrimaryBorder"
            mb={2}
            width="100%"
          >
            {Object.values(TABS).map(tab => (
              <ListView.Tab
                key={tab}
                onClick={() => setActiveTab(tab)}
                isActive={activeTab === tab}
              >
                <Text textStyle="headingLine">{getTabLabel(tab)}</Text>
              </ListView.Tab>
            ))}
          </Flex>
          {renderTab(activeTab)}
        </Flex>
      </Box>
      <Footer />
      <ScrollToTopButton />
    </Box>
  );
};

export default MySubmissionsList;
